
import { Canvas, Circle as FabricCircle, Ellipse, Text, Group } from "fabric";
import { pixelsToMillimeters } from "@/utils/measurement-utils";
import { MeasurementService } from "@/services/measurement-service";
import { createMeasurementLabelGroup, updateMeasurementText } from "./measurement-label-utils";
import { createMeasuredGroup } from "./group-utils";
import { CustomFabricObject } from "@/types/fabric";

export const updateCircleMeasurements = (circle: FabricCircle | Ellipse, label: Text | Group, canvas: Canvas) => {
  if (!circle) return;

  const parent = circle.group;
  const parentScale = parent ? (parent.scaleX || 1) : 1;
  
  let diameter;
  if (circle instanceof FabricCircle) {
    diameter = circle.radius! * 2 * parentScale;
  } else {
    // For ellipse, use the average of rx and ry for display
    const avgRadius = ((circle.rx || 0) + (circle.ry || 0)) / 2;
    diameter = avgRadius * 2 * parentScale;
  }
  
  const diameterMm = Math.round(pixelsToMillimeters(diameter, canvas));

  // Update the text content with consistent styling
  MeasurementService.updateMeasurementText(label, `∅${diameterMm}mm`);

  canvas.renderAll();
};

export const createMeasuredCircle = (canvas: Canvas, radius: number, props: any, isOval: boolean = false) => {
  let shape;
  
  if (isOval) {
    // Create an ellipse with 1.5:1 aspect ratio
    shape = new Ellipse({
      ...props,
      rx: radius * 1.5,  // Make the x-radius larger for oval shape
      ry: radius,        // Keep the y-radius as specified
    });
  } else {
    // Regular circle
    shape = new FabricCircle({
      ...props,
      radius
    });
  }
  
  // Set data properties for identification
  (shape as CustomFabricObject).data = {
    ...(shape as CustomFabricObject).data,
    isOval: isOval
  };

  // Calculate diameter in millimeters for the measurement label
  const diameterMm = Math.round(
    pixelsToMillimeters(isOval ? (radius * 1.5 + radius) : radius * 2, canvas)
  );
  
  // Get current zoom for scaling
  const zoom = canvas.getZoom();
  
  // Position label directly in the center of the shape
  const label = MeasurementService.createMeasurementLabel(
    `∅${diameterMm}mm`,
    props.left,
    props.top,
    0,
    true,
    zoom
  );

  // Create a group with the shape and its measurement
  const group = MeasurementService.createMeasurementGroup(
    shape, 
    [label], 
    canvas, 
    isOval ? 'oval' : 'circle'
  );
  
  return group;
};
